/**
 * initDrawer()
 *
 * Initializes a multi-level off-canvas drawer system.
 * This allows users to open and close sliding panels (drawers) with proper
 * backdrop support, body class toggling, and nested navigation control.
 *
 * Usage:
 * - Drawers are toggled using elements with `data-drawer-trigger` and `data-drawer-target="drawer-id"`.
 * - Drawers must have `data-level` to determine nesting.
 * - Nested drawers will automatically close when a parent or sibling is toggled.
 */

export default function initDrawer() {
    const backdrop = createBackdrop();
    let drawerStack = [];
    let backdropVisible = false;

    console.log('initDrawer called');

    function createBackdrop() {
        const el = document.createElement("div");
        el.classList.add("drawer__backdrop");
        return el;
    }

    function showBackdrop() {
        backdropVisible = true;
        if (!document.body.contains(backdrop)) {
            document.body.appendChild(backdrop);
        }
        requestAnimationFrame(() => {
            backdrop.classList.add("is-active");
        });

        backdrop.addEventListener("click", () => closeTopDrawer(true), {
            once: true,
        });
    }

    function hideBackdrop() {
        backdropVisible = false;
        backdrop.classList.remove("is-active");
        backdrop.addEventListener(
            "transitionend",
            () => {
                if (!backdropVisible && document.body.contains(backdrop)) {
                    document.body.removeChild(backdrop);
                }
            },
            { once: true }
        );
    }

    function updateBodyClass() {
        document.body.classList.toggle(
            "DRAWER--ACTIVE",
            drawerStack.length > 0
        );
    }

    function closeTopDrawer(triggeredByBackdrop = false) {
        const drawer = drawerStack.pop();
        if (!drawer) return;

        drawer.classList.remove("is-active");

        const toggler = document.querySelector(
            `[data-drawer-target="${drawer.id}"]`
        );
        if (toggler) toggler.classList.remove("is-active");

        if (drawerStack.length === 0 || triggeredByBackdrop) {
            hideBackdrop();
        }

        updateBodyClass();
    }

    function closeDrawerAndChildren(fromLevel) {
        const toClose = drawerStack.filter((d) => {
            const level = parseInt(d.getAttribute("data-level"), 10);
            return level >= fromLevel;
        });

        toClose.forEach((drawer) => {
            drawer.classList.remove("is-active");
            const toggler = document.querySelector(
                `[data-drawer-target="${drawer.id}"]`
            );
            if (toggler) toggler.classList.remove("is-active");
        });

        drawerStack = drawerStack.filter((d) => {
            const level = parseInt(d.getAttribute("data-level"), 10);
            return level < fromLevel;
        });
    }

    function toggleDrawer(toggler) {
        const targetId = toggler.getAttribute("data-drawer-target");
        const drawer = document.getElementById(targetId);
        if (!drawer) return;

        const level = parseInt(drawer.getAttribute("data-level"), 10);

        document
            .querySelectorAll(`[data-level="${level}"].is-active`)
            .forEach((el) => {
                if (el !== drawer) {
                    el.classList.remove("is-active");
                    const siblingToggler = document.querySelector(
                        `[data-drawer-target="${el.id}"]`
                    );
                    if (siblingToggler)
                        siblingToggler.classList.remove("is-active");
                    drawerStack = drawerStack.filter((d) => d !== el);
                }
            });

        const isNowActive = drawer.classList.toggle("is-active");
        toggler.classList.toggle("is-active", isNowActive);

        if (isNowActive) {
            drawerStack.push(drawer);
        } else {
            closeDrawerAndChildren(level);
        }

        drawerStack.length > 0 ? showBackdrop() : hideBackdrop();
        updateBodyClass();
    }

    function setupTogglerEvents() {
        document
            .querySelectorAll("[data-drawer-trigger]")
            .forEach((toggler) => {
                const isHoverToggle = toggler.classList.contains(
                    "drawer__hover-toggle"
                );

                if (isHoverToggle && typeof MNK !== "undefined" && MNK) {
                    toggler.addEventListener("mouseenter", () =>
                        openDrawerIfHidden(toggler)
                    );
                } else {
                    toggler.addEventListener("click", (e) => {
                        toggleDrawer(toggler);
                        e.stopPropagation();
                    });
                }
            });
    }

    function openDrawerIfHidden(toggler) {
        const drawer = document.getElementById(
            toggler.getAttribute("data-drawer-target")
        );
        if (drawer && !drawer.classList.contains("is-active")) {
            toggleDrawer(toggler);
        }
    }

    function setupCloseEvents() {
        document.querySelectorAll("[data-drawer-close]").forEach((button) => {
            button.addEventListener("click", (e) => {
                closeTopDrawer();
                e.stopPropagation();
            });
        });
    }

    function handleEscapeKey() {
        document.addEventListener("keydown", (event) => {
            if (
                (event.key === "Escape" || event.keyCode === 27) &&
                drawerStack.length > 0
            ) {
                closeTopDrawer();
            }
        });
    }

    setupTogglerEvents();
    setupCloseEvents();
    handleEscapeKey();
}
